import { computed, onActivated, onScopeDispose, ref, type ComputedRef } from 'vue';

const now = ref(new Date());
let subscribers = 0;
let timer: ReturnType<typeof setTimeout> | null = null;

function tick() {
  now.value = new Date();
}

function clearTimer() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}

function scheduleNext() {
  clearTimer();
  const current = now.value;
  const msUntilNextMinute = (60 - current.getSeconds()) * 1000 - current.getMilliseconds();
  timer = setTimeout(
    () => {
      tick();
      scheduleNext();
    },
    Math.max(msUntilNextMinute, 0),
  );
}

function refresh() {
  tick();
  scheduleNext();
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible') {
    refresh();
  }
}

function start() {
  refresh();
  document.addEventListener('visibilitychange', onVisibilityChange);
}

function stop() {
  clearTimer();
  document.removeEventListener('visibilitychange', onVisibilityChange);
}

/**
 * A single app-wide "now" that ticks on the next wall-clock minute, when the
 * app comes to the foreground, and when a keep-alive subscriber is shown again.
 * Relative labels (today, tomorrow, soon) read it so they recompute instead of
 * freezing at first render.
 */
export function useMinuteClock(): ComputedRef<Date> {
  if (subscribers++ === 0) {
    start();
  }

  onActivated(() => {
    refresh();
  });

  onScopeDispose(() => {
    if (--subscribers === 0) {
      stop();
    }
  });

  return computed(() => now.value);
}
